import React, { useCallback, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import { UserData, CsvValidationError } from '@/school-management/types/user-data';
import { Trans } from 'react-i18next';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import classNames from './CsvDropzone.module.scss';
import classnames from 'classnames';
import uploadIcon from '@/assets/svg/upload.svg';
import checkIcon from '@/assets/svg/check.svg';
import closeIcon from '@/assets/svg/closeFill.svg';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import SVG from 'react-inlinesvg';
import ValidateUser from '@/school-management/utils/userDataValidationUtils';
import AppLoaderCircle from '@/components/AppLoaderCircle';

function fileValidator(file: File): CsvValidationError | null {
  const validTypes = [
    'text/csv',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ];
  const maxSize = 256 * 1024; // 256 KB in bytes

  if (!validTypes.includes(file.type)) {
    return {
      code: 'wrong_format',
      message: '',
    };
  }

  if (file.size > maxSize) {
    return {
      code: 'file_too_large',
      message: '',
    };
  }

  return null;
}

const validateRowContent = (
  rows: UserData[],
  limit: number,
  isStudents: boolean
): { isValid: boolean; reason?: string } => {
  if (rows.length === 0) {
    return { isValid: false, reason: 'file_empty' };
  }

  // The limit on the maximum number of users only exists for students, not for teachers.
  if (rows.length > limit && isStudents) {
    return {
      isValid: false,
      reason: isStudents ? 'max_number_of_students' : 'max_number_of_teachers',
    };
  }

  for (const row of rows) {
    const validationErrors = ValidateUser(row);
    if (validationErrors.length > 0) {
      const messages = validationErrors.map(e => e.message);

      if (messages.includes('first_name_too_long')) {
        return { isValid: false, reason: 'first_name_too_long' };
      }

      if (messages.includes('last_name_too_long')) {
        return { isValid: false, reason: 'last_name_too_long' };
      }
      return { isValid: false, reason: 'bad_formatting' };
    }
  }
  return { isValid: true };
};

interface CsvDropzoneProps {
  usersData: UserData[];
  setUsersData: React.Dispatch<React.SetStateAction<UserData[]>>;
  isCsvLoading: boolean;
  setIsCsvLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setCsvFilesAdded: React.Dispatch<React.SetStateAction<boolean>>;
  usersInClass?: number;
  type: 'students' | 'teachers';
}

export interface CsvDropzoneRef {
  removeFile: () => void;
}

const CsvDropzone = forwardRef<CsvDropzoneRef, CsvDropzoneProps>(
  (
    { setUsersData, isCsvLoading, setIsCsvLoading, setCsvFilesAdded, usersInClass = 0, type },
    ref
  ) => {
    const { t } = useAdminTranslation();
    const [files, setFiles] = useState<File[]>([]);
    const [fileRejections, setFileRejections] = useState<FileRejection[]>([]);
    const [parseError, setParseError] = useState<CsvValidationError | null>(null);

    const maxUsersNumberInClass = 50;
    const allowedUsersNumber = maxUsersNumberInClass - usersInClass;
    const isStudents = type === 'students';
    const i18nPrefix = isStudents ? 'settings.add_students' : 'settings.add_teachers';

    const onDrop = useCallback((acceptedFiles: File[], fileRejections: FileRejection[]) => {
      setIsCsvLoading(true);
      setCsvFilesAdded(true);
      setFiles(acceptedFiles);
      setFileRejections(fileRejections);
      setTimeout(() => {
        setIsCsvLoading(false);
      }, 2000);
    }, []);

    const { getRootProps, getInputProps, open } = useDropzone({
      noClick: true,
      noKeyboard: true,
      maxFiles: 1,
      validator: fileValidator,
      onDrop,
    });

    useEffect(() => {
      if (files.length > 0) {
        const file = files[0];
        const fileType = file.name.split('.').pop()?.toLowerCase();

        if (fileType === 'csv') {
          parseCSV(file);
        } else if (['xls', 'xlsx'].includes(fileType || '')) {
          parseXLSX(file);
        } else {
          setParseError({
            code: 'wrong_format',
            message: 'wrong_format',
          });
          setUsersData([]);
        }
      }
    }, [files]);

    const parseCSV = (file: File) => {
      Papa.parse(file, {
        complete: result => handleParseComplete(result.data),
        header: true,
        skipEmptyLines: true,
        error: error => setParseError({ code: 'bad_formatting', message: error.message }),
      });
    };

    const parseXLSX = (file: File) => {
      const reader = new FileReader();
      reader.onload = e => {
        if (e?.target?.result) {
          const data = new Uint8Array(e.target.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          handleParseComplete(jsonData);
        }
      };

      reader.readAsArrayBuffer(file);
    };

    const handleParseComplete = (data: any) => {
      const validationResult = validateRowContent(data, allowedUsersNumber, isStudents);

      if (validationResult.isValid) {
        setUsersData(data);
        setParseError(null);
      } else {
        setParseError({
          code: validationResult.reason || '',
          message: validationResult.reason || '',
        });

        setUsersData([]);
      }
    };

    const removeFile = useCallback(() => {
      setFiles([]);
      setFileRejections([]);
      setUsersData([]);
      setParseError(null);
      setCsvFilesAdded(false);
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        removeFile,
      }),
      [removeFile]
    );

    return (
      <div className='container'>
        <div {...getRootProps({ className: classNames.dropzone })}>
          <input {...getInputProps()} />
          {files.length > 0 && !parseError && !isCsvLoading && (
            <div className={classNames.inner}>
              <SVG src={checkIcon} className={`${classNames.checkmark} ${classNames.success}`} />
              <span className={classNames.uploadText}>{t(`${i18nPrefix}.upload_successful`)}</span>
              <span className={classNames.badge}>
                {files[0].name}{' '}
                <SVG src={closeIcon} onClick={removeFile} className={classNames.removeFile} />
              </span>
            </div>
          )}

          {parseError && !isCsvLoading && (
            <div className={classNames.inner}>
              <SVG src={closeIcon} className={`${classNames.checkmark} ${classNames.failure}`} />
              <span className={classNames.uploadText}>
                <Trans
                  i18nKey={`${i18nPrefix}.errors.${parseError.code}`}
                  ns={['admin', 'translation']}
                ></Trans>
              </span>
              <span className={classNames.badge}>
                {files.length > 0 ? files[0].name : ''}{' '}
                <SVG src={closeIcon} onClick={removeFile} className={classNames.removeFile} />
              </span>
            </div>
          )}

          {fileRejections.length > 0 && !isCsvLoading && (
            <div className={classNames.inner}>
              <SVG src={closeIcon} className={`${classNames.checkmark} ${classNames.failure}`} />
              {fileRejections.length > 0 && fileRejections[0].errors.length > 0 && (
                <span className={classNames.uploadText}>
                  <Trans
                    i18nKey={`${i18nPrefix}.errors.${fileRejections[0].errors[0].code}`}
                    ns={['admin', 'translation']}
                  ></Trans>
                </span>
              )}
              <span className={classNames.badge}>
                {fileRejections[0].file.name}{' '}
                <SVG src={closeIcon} onClick={removeFile} className={classNames.removeFile} />
              </span>
            </div>
          )}

          {files.length === 0 && fileRejections.length === 0 && !isCsvLoading && (
            <div className={classNames.inner}>
              <SVG src={uploadIcon} />
              <span className={classNames.uploadText}>
                {t(`${i18nPrefix}.drag_here`)}
                <button
                  type='button'
                  onClick={open}
                  className={classnames(classNames.link, 'browse-files-button')}
                  aria-label={t(`${i18nPrefix}.click_to_browse`).toString()}
                >
                  {t(`${i18nPrefix}.click_to_browse`)}
                </button>
              </span>
              {isStudents && (
                <span
                  className={classNames.attentionText}
                  dangerouslySetInnerHTML={{
                    __html: t(
                      usersInClass
                        ? `${i18nPrefix}.upload_attention_text`
                        : `${i18nPrefix}.upload_attention_text_new_class`,
                      {
                        limit: maxUsersNumberInClass,
                        curent: usersInClass,
                        certified: allowedUsersNumber,
                      }
                    ).toString(),
                  }}
                />
              )}
            </div>
          )}

          {isCsvLoading && (
            <div className={classNames.inner}>
              <AppLoaderCircle className='m-auto' />
            </div>
          )}
        </div>
      </div>
    );
  }
);

export default CsvDropzone;
